// Zod schema + defaults + draft helpers for the Target list composer.
// Field-level rules (non-empty mandate, min≤max, numeric, source caps) run through
// RHF `form.handleSubmit` + zodResolver at submit — same gate Strategic uses.
import { z } from 'zod';
import type { TargetListForm } from '@/types';
import {
  addSizeRangeIssues,
  addSourceCapIssues,
  optionalNumeric,
} from '@/features/research/formValidation';

const locationGroupSchema = z.object({
  continent: z.string().optional(),
  country: z.string().optional(),
  region: z.string().optional(),
  state: z.string().optional(),
  city: z.string().optional(),
});

export const targetListSchema = z
  .object({
    businessQuery: z
      .array(
        z.object({ value: z.string().max(5000, 'Description must be 5000 characters or fewer') }),
      )
      .min(1),
    industry: z.string(),
    subIndustry: z.string(),
    primaryActivity: z.enum(['Product', 'Service', 'Both', '']),
    secondaryActivity: z.enum(['Manufacturer', 'Distributor', 'Retailer', 'Not Applicable', '']),
    primaryBusinessOnly: z.boolean(),
    skipWebsiteScraping: z.boolean(),
    excludeTerms: z.array(z.string()),
    size: z.object({
      minRevenue: optionalNumeric,
      maxRevenue: optionalNumeric,
      minEmployees: optionalNumeric,
      maxEmployees: optionalNumeric,
      sizeCriteriaLogic: z.enum(['AND', 'OR']),
    }),
    geography: z.array(locationGroupSchema),
    customInsights: z.object({
      scrapingPath: z.literal('strategic').catch('strategic'),
      questions: z.array(z.object({ value: z.string() })),
    }),
    enrichment: z.object({
      useNews: z.boolean(),
      useApollo: z.boolean(),
      apolloEnrichMode: z.enum(['none', 'contacts', 'company_data', 'all']),
      enableLinkedinEnrichment: z.boolean(),
      ownershipEnrichment: z.boolean(),
      acquisitionEnrichment: z.boolean(),
      blankFieldBackfill: z.boolean(),
      acquisitionTargetReadiness: z.boolean(),
      sellSideMandateReadiness: z.boolean(),
      buyerPropensityScore: z.boolean(),
    }),
    llm: z.object({
      useDefault: z.boolean(),
      providers: z.object({
        openai: z.boolean(),
        anthropic: z.boolean(),
        google: z.boolean(),
      }),
      enableFallback: z.boolean(),
    }),
    additionalSearch: z.object({
      enableApolloSearch: z.boolean(),
      apolloMaxResults: z.string().optional(),
      enableGmapsSearch: z.boolean(),
      gmapsMaxResults: z.string().optional(),
      enableCoresignalSearch: z.boolean(),
      coresignalMaxResults: z.string().optional(),
      enableLinkedinSearch: z.boolean(),
      linkedinMaxResults: z.string().optional(),
      enableFindallSearch: z.boolean(),
      findallMaxResults: z.string().optional(),
    }),
  })
  .superRefine((data, ctx) => {
    if (!data.businessQuery.some((q) => q.value.trim())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['businessQuery', 0, 'value'],
        message: 'Add a target description before building.',
      });
    }

    addSizeRangeIssues(ctx, data.size);
    addSourceCapIssues(ctx, data.additionalSearch);
  });

export const DRAFT_KEY = 'quralyst:draft:target_list';

export const targetListDefaults: TargetListForm = {
  businessQuery: [{ value: '' }],
  industry: '',
  subIndustry: '',
  primaryActivity: '',
  secondaryActivity: '',
  primaryBusinessOnly: false,
  skipWebsiteScraping: false,
  excludeTerms: [],
  size: {
    minRevenue: '',
    maxRevenue: '',
    minEmployees: '',
    maxEmployees: '',
    sizeCriteriaLogic: 'AND',
  },
  geography: [{ continent: '', country: '', state: '', city: '' }],
  customInsights: { scrapingPath: 'strategic', questions: [] },
  enrichment: {
    useNews: false,
    useApollo: false,
    apolloEnrichMode: 'none',
    enableLinkedinEnrichment: false,
    ownershipEnrichment: false,
    acquisitionEnrichment: false,
    blankFieldBackfill: false,
    acquisitionTargetReadiness: false,
    sellSideMandateReadiness: false,
    buyerPropensityScore: false,
  },
  llm: {
    useDefault: true,
    providers: { openai: true, anthropic: true, google: true },
    enableFallback: true,
  },
  additionalSearch: {
    enableApolloSearch: true,
    apolloMaxResults: '50',
    enableGmapsSearch: false,
    gmapsMaxResults: '50',
    enableCoresignalSearch: false,
    coresignalMaxResults: '50',
    enableLinkedinSearch: false,
    linkedinMaxResults: '50',
    enableFindallSearch: false,
    findallMaxResults: '50',
  },
};

/** Merge a (possibly partial / stale) draft over defaults so missing keys never crash RHF. */
function mergeDraft(parsed: Partial<TargetListForm>): TargetListForm {
  return {
    ...targetListDefaults,
    ...parsed,
    excludeTerms: parsed.excludeTerms ?? targetListDefaults.excludeTerms,
    size: { ...targetListDefaults.size, ...parsed.size },
    customInsights: {
      ...targetListDefaults.customInsights,
      ...parsed.customInsights,
      scrapingPath: 'strategic',
    },
    enrichment: { ...targetListDefaults.enrichment, ...parsed.enrichment },
    llm: {
      ...targetListDefaults.llm,
      ...parsed.llm,
      providers: { ...targetListDefaults.llm.providers, ...parsed.llm?.providers },
    },
    additionalSearch: { ...targetListDefaults.additionalSearch, ...parsed.additionalSearch },
    businessQuery:
      parsed.businessQuery && parsed.businessQuery.length
        ? parsed.businessQuery
        : targetListDefaults.businessQuery,
    geography:
      parsed.geography && parsed.geography.length ? parsed.geography : targetListDefaults.geography,
  };
}

/**
 * One-shot handoff for "Use settings" / Edit criteria.
 * Uses sessionStorage (not sticky localStorage) so a normal browser refresh starts clean.
 */
export function stashDraft(values: unknown): void {
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(values));
    localStorage.removeItem(DRAFT_KEY);
  } catch {
    /* storage full / unavailable — ignore */
  }
}

/** Read + clear a one-shot stashed draft. Returns defaults when none is present. */
export function loadDraft(): TargetListForm {
  try {
    localStorage.removeItem(DRAFT_KEY);
    const raw = sessionStorage.getItem(DRAFT_KEY);
    if (!raw) return targetListDefaults;
    sessionStorage.removeItem(DRAFT_KEY);
    return mergeDraft(JSON.parse(raw) as Partial<TargetListForm>);
  } catch {
    return targetListDefaults;
  }
}

/** No sticky drafts — kept for call-site compatibility; clears any leftover storage. */
export function saveDraft(_values: TargetListForm): void {
  void _values;
  clearDraft();
}

export function clearDraft(): void {
  try {
    localStorage.removeItem(DRAFT_KEY);
    sessionStorage.removeItem(DRAFT_KEY);
  } catch {
    /* storage unavailable — ignore */
  }
}
